import {LandPlot} from "../../interfaces/card";
import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import Geometry from "@arcgis/core/geometry/Geometry";
import { removeBasePath } from "../../utils/path-change";

interface MultiplePlotList {
    initialPlot: LandPlot | null,
    sourceUrl: string | null,
    adjacentPlots: LandPlot[],
    plotsToMerge: LandPlot[],
    filledPlots: LandPlot[],
    finalMultiplePlot: LandPlot | null,
    generalGeometry: Geometry | null
}

const initialState: MultiplePlotList = {
    initialPlot: null,
    sourceUrl: null,
    adjacentPlots: [],
    plotsToMerge: [],
    filledPlots: [],
    finalMultiplePlot: null,
    generalGeometry: null
}

export const multiplePlotListSlice = createSlice({
    name: 'multiplePlotList',
    initialState,
    reducers: {
        setInitialPlot: (state, action: PayloadAction<{ plot: LandPlot, url: string}>) => {
            const urlObj = new URL(action.payload.url);
            const modifiedPath = removeBasePath(urlObj.pathname);
            state.initialPlot = action.payload.plot;
            state.sourceUrl = modifiedPath + urlObj.search;
            state.finalMultiplePlot = { ...action.payload.plot };
            state.plotsToMerge = initialState.plotsToMerge;
            state.adjacentPlots = initialState.adjacentPlots;
            state.filledPlots = initialState.filledPlots;
        },
        setAdjacentPlot: (state, action: PayloadAction<LandPlot[]>) => {
            state.adjacentPlots = action.payload.filter(plot => plot.status.toLowerCase() === 'vacant');
            state.filledPlots = action.payload.filter(plot => plot.status.toLowerCase() !== 'vacant');
        },
        setPlotToMerge: (state, action: PayloadAction<LandPlot>) => {
            state.plotsToMerge = [...state.plotsToMerge, {...action.payload}];
            state.adjacentPlots = state.adjacentPlots.filter(plot => plot.fid !== action.payload.fid);
            if (state.finalMultiplePlot) {
                state.finalMultiplePlot.number = `${state.finalMultiplePlot.number}, ${action.payload.number}`;
                let types = state.finalMultiplePlot.typeOfUse
                    ? state.finalMultiplePlot.typeOfUse.split(', ').filter(item => item)
                    : [];
                if (!types.includes(action.payload.typeOfUse)) {
                    types.push(action.payload.typeOfUse);
                }
                state.finalMultiplePlot.typeOfUse = types.join(', ');
            }
        },
        deletePlotToMerge: (state, action: PayloadAction<LandPlot>) => {
            state.plotsToMerge = state.plotsToMerge.filter(plot => plot.fid !== action.payload.fid);
            state.adjacentPlots.push(action.payload);

            if (state.finalMultiplePlot) {
                const numbers = state.finalMultiplePlot.number.split(', ').filter(num => num !== action.payload.number);
                state.finalMultiplePlot.number = numbers.join(', ');
                const typesSet = new Set<string>();
                if (state.initialPlot) {
                    typesSet.add(state.initialPlot.typeOfUse);
                }
                state.plotsToMerge.forEach(plot => {
                    typesSet.add(plot.typeOfUse);
                });
                state.finalMultiplePlot.typeOfUse = Array.from(typesSet).join(', ');
            }

            if (state.plotsToMerge.length === 0 && state.initialPlot) {
                if (state.finalMultiplePlot) {
                    state.finalMultiplePlot.area = state.initialPlot.area;
                }
            }
        },
        clearAllPlots: (state) => {
            state.plotsToMerge = initialState.plotsToMerge;
            state.adjacentPlots = initialState.adjacentPlots;
            state.filledPlots = initialState.filledPlots;
            state.finalMultiplePlot = initialState.finalMultiplePlot;
            state.initialPlot = initialState.initialPlot;
            state.sourceUrl = initialState.sourceUrl;
        },
        setMergedGeometry: (state, action: PayloadAction<any>) => {
            if (state.finalMultiplePlot) {
                state.finalMultiplePlot.geometry = action.payload;
            }
        },
        setGeneralGeometry: (state, action: PayloadAction<any>) => {
            state.generalGeometry = action.payload;
        },
        setAreaFinalMultiplePlot: (state, action: PayloadAction<any>) => {
            if (state.finalMultiplePlot) {
                if (state.plotsToMerge.length === 0 && state.initialPlot) {
                    state.finalMultiplePlot.area = state.initialPlot.area;
                } else {
                    state.finalMultiplePlot.area = action.payload;
                }
            }
        }
    }
})

export const {
    setInitialPlot,
    setAdjacentPlot,
    setPlotToMerge,
    deletePlotToMerge,
    clearAllPlots,
    setMergedGeometry,
    setAreaFinalMultiplePlot,
    setGeneralGeometry,
} = multiplePlotListSlice.actions;

export default multiplePlotListSlice.reducer;